import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../services/apiBase';

type AdminTab = 'reports' | 'users' | 'listings';
type ReportStatus = 'open' | 'under_review' | 'resolved' | 'dismissed';

const API_URL = getApiUrl();
const reportStatuses: ReportStatus[] = ['open', 'under_review', 'resolved', 'dismissed'];
const statusLabels: Record<ReportStatus, string> = {
  open: '待处理',
  under_review: '处理中',
  resolved: '已处理',
  dismissed: '已驳回',
};

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
});

const AdminDashboard: React.FC = () => {
  const [tab, setTab] = useState<AdminTab>('reports');
  const [overview, setOverview] = useState({ openReports: 0, suspendedUsers: 0, moderatedListings: 0 });
  const [reports, setReports] = useState<any[]>([]);
  const [listings, setListings] = useState<any[]>([]);
  const [userEmail, setUserEmail] = useState('');
  const [userResult, setUserResult] = useState<any>(null);
  const [listingQuery, setListingQuery] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadOverview = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/admin/overview`, authConfig());
    setOverview(data);
  }, []);

  const loadReports = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/admin/reports`, authConfig());
    setReports(data.reports || []);
  }, []);

  const loadListings = useCallback(async (query = '') => {
    const { data } = await axios.get(`${API_URL}/admin/listings`, {
      ...authConfig(),
      params: query.trim() ? { q: query.trim() } : {},
    });
    setListings(data.listings || []);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadOverview(), loadReports(), loadListings('')]);
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || '无法加载管理数据，请刷新重试。');
    } finally {
      setLoading(false);
    }
  }, [loadListings, loadOverview, loadReports]);

  useEffect(() => { void refresh(); }, [refresh]);

  const runAction = async (url: string, payload: Record<string, unknown>, reload: () => Promise<void>) => {
    if (reason.trim().length < 10) {
      setError('请填写至少 10 个字的处理原因。');
      return;
    }
    setError('');
    setNotice('');
    try {
      await axios.patch(`${API_URL}${url}`, { ...payload, reason: reason.trim() }, authConfig());
      setNotice('操作已记录。');
      setReason('');
      await Promise.all([reload(), loadOverview()]);
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || '操作失败，请重试。');
    }
  };

  const findUser = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setUserResult(null);
    try {
      const { data } = await axios.get(`${API_URL}/admin/users`, {
        ...authConfig(),
        params: { email: userEmail.trim() },
      });
      setUserResult(data.user);
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || '查找用户失败。');
    }
  };

  const searchListings = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadListings(listingQuery);
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">管理员后台</h1>
            <p className="mt-1 text-slate-600">处理举报、管理商品和学生账户。</p>
          </div>
          <button type="button" onClick={() => void refresh()} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">刷新数据</button>
        </div>

        <section aria-label="管理概览" className="mb-7 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl bg-white p-5 shadow-sm"><p className="text-sm text-slate-500">待处理举报</p><p className="mt-2 text-3xl font-semibold text-slate-900">{overview.openReports}</p></div>
          <div className="rounded-xl bg-white p-5 shadow-sm"><p className="text-sm text-slate-500">暂停账户</p><p className="mt-2 text-3xl font-semibold text-slate-900">{overview.suspendedUsers}</p></div>
          <div className="rounded-xl bg-white p-5 shadow-sm"><p className="text-sm text-slate-500">管理员下架商品</p><p className="mt-2 text-3xl font-semibold text-slate-900">{overview.moderatedListings}</p></div>
        </section>

        <div className="mb-5 flex flex-wrap gap-2" role="tablist" aria-label="管理功能">
          {([['reports', '举报队列'], ['users', '用户管理'], ['listings', '商品管理']] as [AdminTab, string][]).map(([value, label]) => (
            <button key={value} type="button" role="tab" aria-selected={tab === value} onClick={() => setTab(value)} className={`rounded-lg px-4 py-2 text-sm font-medium ${tab === value ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700 shadow-sm hover:bg-slate-100'}`}>{label}</button>
          ))}
        </div>

        {(error || notice) && <div role={error ? 'alert' : 'status'} className={`mb-4 rounded-lg p-3 text-sm ${error ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>{error || notice}</div>}
        {loading ? <p className="rounded-xl bg-white p-6 text-slate-600">正在加载…</p> : (
          <section className="rounded-xl bg-white p-5 shadow-sm sm:p-6">
            <label className="mb-5 block max-w-xl text-sm font-medium text-slate-700">
              操作原因（至少 10 个字）
              <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={2} maxLength={1000} className="mt-1 block w-full rounded-lg border border-slate-300 p-3 text-slate-900" />
            </label>

            {tab === 'reports' && <div className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-900">举报队列</h2>
              {reports.length === 0 ? <p className="text-slate-600">目前没有举报。</p> : reports.map((report) => (
                <article key={report._id} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{report.listing?.title || `私信对话 ${report.conversationId || ''}`}</p>
                      <p className="mt-1 text-sm text-slate-600">举报人：{report.reporter?.email || '未知'} · 被举报：{report.reportedUser?.email || '未知'}</p>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{report.reason}</p>
                      <p className="mt-2 text-xs text-slate-500">{new Date(report.createdAt).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select aria-label="举报处理状态" value={report.status} onChange={(event) => setReports((current) => current.map((item) => item._id === report._id ? { ...item, status: event.target.value } : item))} className="rounded border border-slate-300 p-2 text-sm">
                        {reportStatuses.map((status) => <option key={status} value={status}>{statusLabels[status]}</option>)}
                      </select>
                      <button type="button" onClick={() => void runAction(`/admin/reports/${report._id}`, { status: report.status }, loadReports)} className="rounded bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700">保存处理</button>
                    </div>
                  </div>
                </article>
              ))}
            </div>}

            {tab === 'users' && <div>
              <h2 className="mb-4 text-xl font-semibold text-slate-900">按邮箱查找用户</h2>
              <form onSubmit={findUser} className="mb-5 flex max-w-2xl flex-wrap gap-2">
                <input type="email" required value={userEmail} onChange={(event) => setUserEmail(event.target.value)} placeholder="student@school.edu" className="min-w-64 flex-1 rounded-lg border border-slate-300 px-3 py-2" />
                <button type="submit" className="rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white hover:bg-indigo-700">精确查找</button>
              </form>
              {userResult && <article className="max-w-2xl rounded-lg border border-slate-200 p-4">
                <p className="font-semibold text-slate-900">{userResult.firstName} {userResult.lastName}</p>
                <p className="text-sm text-slate-600">{userResult.email} · {userResult.school || '学校未识别'} · {userResult.eduVerified ? '邮箱已验证' : '邮箱未验证'}</p>
                <p className="mt-2 text-sm">账户状态：{userResult.suspended ? '已暂停' : '正常'}</p>
                <button type="button" onClick={() => void runAction(`/admin/users/${userResult._id}/suspension`, { suspended: !userResult.suspended }, async () => {
                  const { data } = await axios.get(`${API_URL}/admin/users`, { ...authConfig(), params: { email: userResult.email } });
                  setUserResult(data.user);
                })} className="mt-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">{userResult.suspended ? '恢复账户' : '暂停账户'}</button>
              </article>}
            </div>}

            {tab === 'listings' && <div>
              <h2 className="mb-4 text-xl font-semibold text-slate-900">商品管理</h2>
              <form onSubmit={searchListings} className="mb-5 flex max-w-2xl gap-2">
                <input value={listingQuery} onChange={(event) => setListingQuery(event.target.value)} placeholder="按商品标题查找" className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2" />
                <button type="submit" className="rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white hover:bg-indigo-700">查找</button>
              </form>
              <div className="space-y-3">
                {listings.map((listing) => <article key={listing._id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 p-4">
                  <div><p className="font-semibold text-slate-900">{listing.title}</p><p className="text-sm text-slate-600">{listing.seller?.email || '未知用户'} · 状态：{listing.status}</p></div>
                  {listing.status === 'removed' ? <span className="text-sm text-slate-500">用户已删除</span> : <button type="button" onClick={() => void runAction(`/admin/listings/${listing._id}/moderation`, { action: listing.status === 'removed_by_admin' ? 'restore' : 'remove' }, async () => loadListings())} className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${listing.status === 'removed_by_admin' ? 'bg-green-700 hover:bg-green-800' : 'bg-red-600 hover:bg-red-700'}`}>{listing.status === 'removed_by_admin' ? '恢复商品' : '管理员下架'}</button>}
                </article>)}
                {listings.length === 0 && <p className="text-slate-600">没有找到商品。</p>}
              </div>
            </div>}
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
